import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wordmark } from "./Brand";

const easeLux = [0.16, 1, 0.3, 1];

/**
 * 3-second cinematic black intro.
 * Scene 1 (0.0-1.2s) -> Scene 2 (1.2-2.4s) -> Scene 3 (2.4-3.0s+) -> dissolve.
 */
export default function Intro({ onDone }) {
  const [scene, setScene] = useState(0);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setScene(1), 2400);   // line 1 holds ~2.4s
    const t2 = setTimeout(() => setScene(2), 4800);   // line 2 holds ~2.4s
    const t3 = setTimeout(() => setGone(true), 7000);  // logo holds ~2.2s
    const t4 = setTimeout(() => onDone && onDone(), 7600);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [onDone]);

  const line = (text) => (
    <motion.p
      key={text}
      className="font-display text-[#F8F7F3] text-center px-6"
      style={{ letterSpacing: "0.08em", fontWeight: 500 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: easeLux }}
    >
      <span className="text-[28px] md:text-[42px] leading-snug">{text}</span>
    </motion.p>
  );

  return (
    <AnimatePresence>
      {!gone && (
        <motion.div
          data-testid="cinematic-intro"
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: easeLux }}
        >
          <AnimatePresence mode="wait">
            {scene === 0 && line("Trust begins with a name you'll remember")}
            {scene === 1 && line("We want you to remember our name.")}
            {scene === 2 && (
              <motion.div
                key="logo"
                className="relative flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: easeLux }}
              >
                <div
                  className="absolute -z-10 w-[420px] h-[420px] md:w-[560px] md:h-[560px] rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(176,141,87,0.30) 0%, rgba(176,141,87,0.08) 42%, transparent 70%)",
                  }}
                />
                <img
                  src="/agraga-crest.png"
                  alt="AGRAGA Velora emblem"
                  className="w-60 h-60 md:w-80 md:h-80 object-contain"
                  style={{ filter: "drop-shadow(0 12px 44px rgba(176,141,87,0.35))" }}
                />
                <div className="mt-7 flex items-center gap-4">
                  <span className="h-px w-8 bg-[#B08D57]/60" />
                  <Wordmark color="#F8F7F3" spacing="0.34em" size="text-sm md:text-base" />
                  <span className="h-px w-8 bg-[#B08D57]/60" />
                </div>
                <p
                  className="font-display italic text-[#B08D57] mt-5 text-[22px] md:text-[28px]"
                  style={{ letterSpacing: "0.06em" }}
                >
                  Trust. Delivered.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
